import { memo, useCallback, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import {
  formatDateByUTC,
  formatDateOnlyByLocale,
  isoToDateOnly
} from 'utils/formattingUtil';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

dayjs.extend(utc);

export interface DateFilterProps {
  handleUpdateDateParams: (startDate: string, endDate: string) => void;
  isLocalTime?: boolean;
}

export const DateFilter = memo(
  ({ handleUpdateDateParams, isLocalTime = true }: DateFilterProps) => {
    const urlParams = usePaginationFromURL('orders');

    const startDateFromUrl = urlParams.startDate;
    const endDateFromUrl = urlParams.endDate;
    const startDateOnlyFromUrl = useMemo(
      () => isoToDateOnly(startDateFromUrl, false),
      [startDateFromUrl]
    );
    const endDateOnlyFromUrl = useMemo(
      () => isoToDateOnly(endDateFromUrl, false),
      [endDateFromUrl]
    );

    const currentValue = useMemo(() => {
      if (startDateOnlyFromUrl && endDateOnlyFromUrl) {
        return JSON.stringify({
          startDate: startDateOnlyFromUrl,
          endDate: endDateOnlyFromUrl
        });
      }
      return '';
    }, [startDateOnlyFromUrl, endDateOnlyFromUrl]);

    const { displayLabel, displayParts } = useMemo(() => {
      if (startDateFromUrl && endDateFromUrl) {
        const prefix = 'Date';
        const start = isLocalTime
          ? dayjs(startDateOnlyFromUrl)
          : dayjs.utc(startDateFromUrl);
        const end = isLocalTime
          ? dayjs(endDateOnlyFromUrl)
          : dayjs.utc(endDateFromUrl);
        const formatDisplayDate = (dateStr: string) =>
          isLocalTime
            ? formatDateOnlyByLocale(dateStr)
            : formatDateByUTC(dateStr);

        // If same date, show only one date (single date selection)
        if (start.isSame(end, 'day')) {
          const dateStr = formatDisplayDate(
            isLocalTime ? startDateOnlyFromUrl : start.toISOString()
          );
          return {
            displayLabel: `${prefix} | ${dateStr}`,
            displayParts: [prefix, dateStr]
          };
        }
        const dateRange = `${formatDisplayDate(
          isLocalTime ? startDateOnlyFromUrl : start.toISOString()
        )} - ${formatDisplayDate(
          isLocalTime ? endDateOnlyFromUrl : end.toISOString()
        )}`;
        return {
          displayLabel: `${prefix} | ${dateRange}`,
          displayParts: [prefix, dateRange]
        };
      }
      return {
        displayLabel: 'Pick a Date',
        displayParts: undefined
      };
    }, [
      endDateFromUrl,
      endDateOnlyFromUrl,
      isLocalTime,
      startDateFromUrl,
      startDateOnlyFromUrl
    ]);

    const handleUpdateDateParamsRef = useRef(handleUpdateDateParams);
    handleUpdateDateParamsRef.current = handleUpdateDateParams;

    const handleChange = useCallback((value: string) => {
      if (!value) {
        handleUpdateDateParamsRef.current('', '');
        return;
      }

      try {
        const parsed = JSON.parse(value);
        if (parsed.startDate && parsed.endDate) {
          handleUpdateDateParamsRef.current(parsed.startDate, parsed.endDate);
        }
      } catch (error) {
        console.error('Failed to parse date range:', error);
      }
    }, []);

    return (
      <FilterPopover
        label={displayLabel}
        labelParts={displayParts}
        onChange={handleChange}
        selectedItems={currentValue ? [currentValue] : []}
        filterType={FilterType.DATE_PICKER}
        hideIndicator
        isLocalTime={isLocalTime}
      />
    );
  }
);

DateFilter.displayName = 'DateFilter';
